import { buildValidationChecker } from "@vuu-ui/vuu-data-react";
import {
  DataSource,
  TableSchema,
  WithBaseFilter,
  WithFullConfig,
} from "@vuu-ui/vuu-data-types";
import { VuuColumnDataType } from "@vuu-ui/vuu-protocol-types";
import {
  ColumnDescriptor,
  ColumnLayout,
  ResizePhase,
  RuntimeColumnDescriptor,
  TableDisplayAttributes,
  TableConfig,
  TableHeadings,
  TableSelectionModel,
} from "@vuu-ui/vuu-table-types";
import {
  applyFilterToColumns,
  applyGroupByToColumns,
  applyRuntimeColumnWidthsToConfig,
  applySortToColumns,
  applyWidthToColumns,
  assertAllColumnsAreIncludedInSubscription,
  checkConfirmationPending,
  existingSort,
  flattenColumnGroup,
  getCellRenderer,
  getColumnHeaderContentRenderer,
  getColumnHeaderLabelRenderer,
  getColumnLabel,
  getTableHeadings,
  getValueFormatter,
  hasValidationRules,
  isFilteredColumn,
  isGroupColumn,
  isNumericType,
  isPinned,
  logger,
  removeSort,
  replaceColumn,
  sortPinnedColumns,
  stripFilterFromColumns,
} from "@vuu-ui/vuu-utils";
import { Reducer, useReducer } from "react";

const { info } = logger("useTableModel");

const DEFAULT_COLUMN_WIDTH = 100;

const columnWithoutDataType = ({ serverDataType }: ColumnDescriptor) =>
  serverDataType === undefined;

const getDataType = (
  column: ColumnDescriptor,
  tableSchema?: TableSchema,
): VuuColumnDataType | undefined => {
  const schemaColumn = tableSchema?.columns.find(
    ({ name }) => name === column.name,
  );
  if (schemaColumn) {
    return schemaColumn.serverDataType;
  } else {
    return column.serverDataType;
  }
};

const CheckboxColumnDescriptor = (
  selectionModel: TableSelectionModel,
  width = 25,
): ColumnDescriptor => ({
  allowColumnHeaderMenu: false,
  className: selectionModel === "checkbox-disabled" ? "vuuDisabled" : undefined,
  label: "",
  name: "",
  width,
  resizeable: false,
  sortable: false,
  isSystemColumn: true,
  type: {
    name: "checkbox",
    renderer: {
      name: "checkbox-row-selector-cell",
    },
  },
});

const PinnedCheckboxColumnDescriptor = (
  selectionModel: TableSelectionModel,
  width?: number,
): ColumnDescriptor => ({
  ...CheckboxColumnDescriptor(selectionModel, width),
  pin: "left",
});

/**
 * TableModel represents state used internally to manage Table. It is
 * derived initially from the TableConfig provided by user, along with the
 * data-related config from DataSource.
 */
export interface TableModel extends TableDisplayAttributes {
  columns: RuntimeColumnDescriptor[];
  headings: TableHeadings;
}

/**
 * InternalTableModel describes the state managed within the TableModel
 * reducer. It is the same as TableModel but with the addition of a
 * readonly copy of the original TableConfig.
 */
interface InternalTableModel extends TableModel {
  availableWidth: number;
  tableConfig: Readonly<TableConfig>;
}

const getDefaultAlignment = (serverDataType?: VuuColumnDataType) =>
  serverDataType === undefined
    ? undefined
    : isNumericType(serverDataType)
      ? "right"
      : "left";

export interface ColumnActionInit {
  availableWidth: number;
  selectionModel?: TableSelectionModel;
  type: "init";
  tableConfig: TableConfig;
  dataSource: DataSource;
}

export interface ColumnActionHide {
  type: "hideColumns";
  columns: ColumnDescriptor[];
}
export interface ColumnActionRemove {
  type: "removeColumn";
  column: ColumnDescriptor;
}

export interface ColumnActionShow {
  type: "showColumns";
  columns: RuntimeColumnDescriptor[];
}
export interface ColumnActionMove {
  type: "moveColumn";
  column: RuntimeColumnDescriptor;
  moveBy?: 1 | -1;
}

export interface ColumnActionResize {
  type: "resizeColumn";
  column: RuntimeColumnDescriptor;
  phase: ResizePhase;
  width?: number;
}

export interface ColumnActionSetTableSchema {
  type: "setTableSchema";
  tableSchema: TableSchema;
}

export interface ColumnActionUpdate {
  type: "updateColumn";
  column: ColumnDescriptor;
}

export interface ColumnActionUpdateProp {
  align?: ColumnDescriptor["align"];
  column: ColumnDescriptor;
  hidden?: ColumnDescriptor["hidden"];
  label?: ColumnDescriptor["label"];
  resizing?: RuntimeColumnDescriptor["resizing"];
  type: "updateColumnProp";
  width?: ColumnDescriptor["width"];
}

export interface ColumnActionTableConfig
  extends WithBaseFilter<WithFullConfig> {
  confirmed?: boolean;
  type: "tableConfig";
}

export type TableModelAction =
  | ColumnActionHide
  | ColumnActionInit
  | ColumnActionMove
  | ColumnActionResize
  | ColumnActionSetTableSchema
  | ColumnActionShow
  | ColumnActionUpdate
  | ColumnActionUpdateProp
  | ColumnActionTableConfig;

type TableModelReducer = Reducer<InternalTableModel, TableModelAction>;

export type TableModelActionDispatch = (action: TableModelAction) => void;

const tableModelReducer: TableModelReducer = (state, action) => {
  info?.(`TableModelReducer ${action.type}`);
  switch (action.type) {
    case "init": {
      if (
        state.tableConfig.columnLayout === "manual" &&
        action.tableConfig.columnLayout === "fit"
      ) {
        //TODO we're jumping through hoops here when we should just make config a controlled prop

        // Manual columnLayout has been assigned because user has resized one or more columns.
        // It happened during current session so tableConfig still reflects original value.
        return init({
          ...action,
          tableConfig: applyRuntimeColumnWidthsToConfig(
            action.tableConfig,
            state.columns,
          ),
        });
      } else {
        return init(action, state);
      }
    }
    case "moveColumn":
      return moveColumn(state, action);
    case "resizeColumn":
      return resizeColumn(state, action);
    case "setTableSchema":
      return setTableSchema(state, action);
    case "hideColumns":
      return hideColumns(state, action);
    case "showColumns":
      return showColumns(state, action);
    case "updateColumnProp":
      return updateColumnProp(state, action);
    case "tableConfig":
      return updateTableConfig(state, action);
    default:
      console.log(`unhandled action ${action.type}`);
      return state;
  }
};

export interface TableModelHookProps {
  config: TableConfig;
  dataSource: DataSource;
  selectionModel: TableSelectionModel;
  availableWidth: number;
}

export const useTableModel = ({
  config: tableConfigProp,
  dataSource,
  selectionModel,
  availableWidth,
}: TableModelHookProps) => {
  const [state, dispatchTableModelAction] = useReducer(
    tableModelReducer,
    {
      availableWidth,
      tableConfig: tableConfigProp,
      dataSource,
      selectionModel,
    },
    init,
  );

  const { columns, headings, tableConfig, ...tableAttributes } = state;

  return {
    columns,
    dispatchTableModelAction,
    headings,
    tableAttributes,
    tableConfig,
  };
};

type InitialConfig = {
  availableWidth: number;
  columnLayout?: ColumnLayout;
  dataSource: DataSource;
  selectionModel?: TableSelectionModel;
  tableConfig: TableConfig;
};

function init(
  { availableWidth, dataSource, selectionModel, tableConfig }: InitialConfig,
  previousConfig?: InternalTableModel,
): InternalTableModel {
  const { checkboxColumnWidth = 25, columns, ...tableAttributes } = tableConfig;
  const { config: dataSourceConfig, tableSchema } = dataSource;
  const toRuntimeColumnDescriptor = columnDescriptorToRuntimeColumDescriptor(
    tableAttributes,
    tableSchema,
  );

  const runtimeColumns: RuntimeColumnDescriptor[] = [];

  let colIndex = selectionModel?.startsWith("checkbox") ? 2 : 1;
  assertAllColumnsAreIncludedInSubscription(columns, dataSourceConfig.columns);
  for (const column of columns) {
    runtimeColumns.push(
      toRuntimeColumnDescriptor(column, column.hidden ? -1 : colIndex),
    );
    if (!column.hidden) {
      colIndex += 1;
    }
  }

  if (selectionModel?.startsWith("checkbox")) {
    const somePinnedLeft = runtimeColumns.some((col) => col.pin === "left");
    const checkboxCol = toRuntimeColumnDescriptor(
      somePinnedLeft
        ? PinnedCheckboxColumnDescriptor(selectionModel, checkboxColumnWidth)
        : CheckboxColumnDescriptor(selectionModel, checkboxColumnWidth),
      1,
    );
    runtimeColumns.splice(0, 0, checkboxCol);
  }

  const { columnLayout = "static", selectionBookendWidth = 4 } = tableConfig;
  const runtimeColumnsWithLayout = applyWidthToColumns(runtimeColumns, {
    availableWidth,
    columnLayout,
  });

  const columnsInRenderOrder = runtimeColumnsWithLayout.some(isPinned)
    ? sortPinnedColumns(runtimeColumnsWithLayout, selectionBookendWidth)
    : runtimeColumnsWithLayout;

  let state: InternalTableModel = {
    availableWidth,
    columns: columnsInRenderOrder,
    headings: getTableHeadings(columnsInRenderOrder),
    tableConfig,
    ...tableAttributes,
  };
  if (dataSourceConfig) {
    const { columns: _, ...rest } = dataSourceConfig;
    state = updateTableConfig(state, {
      type: "tableConfig",
      ...rest,
      confirmed: checkConfirmationPending(previousConfig),
    });
  }
  return state;
}

const getLabel = (
  label: string,
  columnFormatHeader?: "uppercase" | "capitalize",
): string => {
  if (columnFormatHeader === "uppercase") {
    return label.toUpperCase();
  } else if (columnFormatHeader === "capitalize") {
    return label[0].toUpperCase() + label.slice(1).toLowerCase();
  }
  return label;
};

const columnDescriptorToRuntimeColumDescriptor =
  (tableAttributes: TableDisplayAttributes, tableSchema?: TableSchema) =>
  (column: ColumnDescriptor, ariaColIndex: number): RuntimeColumnDescriptor => {
    const { columnDefaultWidth = DEFAULT_COLUMN_WIDTH, columnFormatHeader } =
      tableAttributes;
    const serverDataType = getDataType(column, tableSchema);
    const {
      align = getDefaultAlignment(serverDataType),
      name,
      label = getColumnLabel(column),
      source = "server",
      width = columnDefaultWidth,
      ...rest
    } = column;

    const columnWithServerDataType: ColumnDescriptor = {
      ...rest,
      align,
      label,
      name,
      serverDataType,
      source,
    };

    const runtimeColumnWithDefaults: RuntimeColumnDescriptor = {
      ...columnWithServerDataType,
      ariaColIndex,
      CellRenderer: getCellRenderer(column),
      HeaderCellContentRenderer: getColumnHeaderContentRenderer(column),
      HeaderCellLabelRenderer: getColumnHeaderLabelRenderer(column),
      clientSideEditValidationCheck: hasValidationRules(column.type)
        ? buildValidationChecker(column.type.rules)
        : undefined,
      label: getLabel(label, columnFormatHeader),
      originalIdx: ariaColIndex,
      valueFormatter: getValueFormatter(columnWithServerDataType),
      width,
    };

    if (isGroupColumn(runtimeColumnWithDefaults)) {
      runtimeColumnWithDefaults.columns = runtimeColumnWithDefaults.columns.map(
        (col) =>
          columnDescriptorToRuntimeColumDescriptor(tableAttributes)(col, -1),
      );
    }

    return runtimeColumnWithDefaults;
  };

function moveColumn(
  state: InternalTableModel,
  // TODO do we ever use this ?
  { column, moveBy }: ColumnActionMove,
) {
  const { columns } = state;
  if (typeof moveBy === "number") {
    const idx = columns.indexOf(column);
    const newColumns = columns.slice();
    const [movedColumns] = newColumns.splice(idx, 1);
    newColumns.splice(idx + moveBy, 0, movedColumns);
    return {
      ...state,
      columns: newColumns,
    };
  }
  return state;
}

function hideColumns(state: InternalTableModel, { columns }: ColumnActionHide) {
  console.log(`[useTableModel] hideColumns ${JSON.stringify(columns)}`);
  if (columns.some((col) => col.hidden !== true)) {
    return columns.reduce<InternalTableModel>((s, c) => {
      if (c.hidden !== true) {
        return updateColumnProp(s, {
          type: "updateColumnProp",
          column: c,
          hidden: true,
        });
      } else {
        return s;
      }
    }, state);
  } else {
    return state;
  }
}
function showColumns(state: InternalTableModel, { columns }: ColumnActionShow) {
  if (columns.some((col) => col.hidden)) {
    return columns.reduce<InternalTableModel>((s, c) => {
      if (c.hidden) {
        return updateColumnProp(s, {
          type: "updateColumnProp",
          column: c,
          hidden: false,
        });
      } else {
        return s;
      }
    }, state);
  } else {
    return state;
  }
}

function resizeColumn(
  state: InternalTableModel,
  { column, phase, width }: ColumnActionResize,
) {
  const type = "updateColumnProp";
  const resizing = phase !== "end";
  switch (phase) {
    case "begin":
      return updateColumnProp(state, { type, column, resizing });
    case "end": {
      const { tableConfig } = state;
      const isFit = tableConfig.columnLayout === "fit";
      let newState: InternalTableModel = isFit
        ? {
            ...state,
            tableConfig: applyRuntimeColumnWidthsToConfig(
              tableConfig,
              state.columns,
            ),
          }
        : state;

      if (column.pin && !column.pinnedWidth && width) {
        // No pinned width means this is not the last column that is pinned left.
        // All left pinned columns following this one must have pinOffset adjusted
        newState = adjustPinOffsets(newState, column, width);
      }
      return updateColumnProp(newState, { type, column, resizing, width });
    }
    case "resize":
      return updateColumnProp(state, { type, column, width });
    default:
      throw Error(`useTableModel.resizeColumn, invalid resizePhase ${phase}`);
  }
}

function setTableSchema(
  state: InternalTableModel,
  { tableSchema }: ColumnActionSetTableSchema,
) {
  const { columns } = state;
  if (columns.some(columnWithoutDataType)) {
    const cols = columns.map((column) => {
      const serverDataType = getDataType(column, tableSchema);
      if (serverDataType === column.serverDataType) {
        return column;
      }
      const columnWithServerDataType = {
        ...column,
        align: column.align ?? getDefaultAlignment(serverDataType),
        serverDataType,
      };
      // valueFormatter depends on serverDataType (e.g. epochtimestampnano
      // values are decoded as nanos), so must be rebuilt.
      return {
        ...columnWithServerDataType,
        valueFormatter: getValueFormatter(columnWithServerDataType),
      };
    });

    return {
      ...state,
      columns: cols,
    };
  } else {
    return state;
  }
}

function adjustPinOffsets(
  state: InternalTableModel,
  column: RuntimeColumnDescriptor,
  width: number,
) {
  const newColumns = state.columns.slice();
  if (column.pin === "left") {
    const diff = width - column.width;
    const colIndex = newColumns.findIndex((col) => col.name === column.name);
    for (let i = colIndex + 1; i < newColumns.length; i++) {
      const pinnedColumn = newColumns.at(i);
      if (
        pinnedColumn?.pin === "left" &&
        typeof pinnedColumn?.pinnedOffset === "number"
      ) {
        newColumns[i] = {
          ...newColumns[i],
          pinnedOffset: pinnedColumn.pinnedOffset + diff,
        };
        if (pinnedColumn.pinnedWidth) {
          // this is the endPin, we're done
          newColumns[i] = {
            ...newColumns[i],
            pinnedWidth: pinnedColumn.pinnedWidth + diff,
          };

          break;
        }
      } else {
        throw Error(
          `[useTableModel] adjustPinOffsets, invalid column pin, no endPin following pinned column`,
        );
      }
    }
  } else if (column.pin === "right") {
    throw "whoaaargh";
  } else {
    throw Error(
      `[useTableModel] adjustPinOffsets, invalid param, column is not pinned `,
    );
  }

  return {
    ...state,
    columns: newColumns,
  };
}

function updateColumnProp(
  state: InternalTableModel,
  action: ColumnActionUpdateProp,
) {
  let { columns, tableConfig } = state;
  const { align, column, hidden, label, resizing, width } = action;
  const targetColumn = columns.find((col) => col.name === column.name);
  if (targetColumn) {
    if (align === "left" || align === "right") {
      columns = replaceColumn(columns, { ...targetColumn, align });
    }
    if (typeof label === "string") {
      columns = replaceColumn(columns, { ...targetColumn, label });
    }
    if (typeof resizing === "boolean") {
      columns = replaceColumn(columns, { ...targetColumn, resizing });
    }
    if (typeof hidden === "boolean") {
      columns = replaceColumn(columns, { ...targetColumn, hidden });
    }
    if (typeof width === "number") {
      columns = replaceColumn(columns, { ...targetColumn, width });

      const targetConfigColumn = tableConfig.columns.find(
        (col) => col.name === column.name,
      );
      if (targetConfigColumn) {
        tableConfig = {
          ...tableConfig,
          columns: replaceColumn<ColumnDescriptor>(tableConfig.columns, {
            ...targetConfigColumn,
            width,
          }),
        };
      }
    }
  }
  return {
    ...state,
    columns,
    tableConfig,
  } as InternalTableModel;
}

// TODO rename to make clear its dataSOurce config
function updateTableConfig(
  state: InternalTableModel,
  {
    confirmed,
    filterSpec,
    groupBy,
    sort,
  }: Omit<ColumnActionTableConfig, "columns">,
) {
  let result = state;

  const { availableWidth, columnLayout = "static" } = state;

  if (groupBy.length > 0) {
    const groupedColumns = applyGroupByToColumns({
      columns: result.columns,
      groupBy,
      confirmed,
      availableWidth,
    });
    const columns = applyWidthToColumns(groupedColumns, {
      availableWidth,
      columnLayout,
    });

    result = {
      ...state,
      columns,
    };
  } else if (
    result.columns.length > 0 &&
    isGroupColumn(result.columns[0]) &&
    confirmed
  ) {
    result = {
      ...state,
      columns: flattenColumnGroup(result.columns),
    };
  }

  if (sort.sortDefs.length > 0) {
    result = {
      ...state,
      columns: applySortToColumns(result.columns, sort),
    };
  } else if (existingSort(result.columns)) {
    result = {
      ...state,
      columns: removeSort(result.columns),
    };
  }

  if (filterSpec.filter.length > 0) {
    result = {
      ...state,
      columns: applyFilterToColumns(result.columns, filterSpec),
    };
  } else if (result.columns.some(isFilteredColumn)) {
    result = {
      ...state,
      columns: stripFilterFromColumns(result.columns),
    };
  }

  return result;
}
